/* ==========================================================================
   COG — Subscription panel (PDP)
   --------------------------------------------------------------------------
   Owns the single `selling_plan` input inside the product form.

   Shopify's native behaviour: an empty `selling_plan` value = one-time
   purchase; a selling plan id = a subscription line item. Recharge reads the
   resulting subscription contract, so this uses the supported path rather than
   talking to the app directly.

   Defensive by design: the value is re-synced immediately before the form is
   submitted, so it stays correct even if another script (e.g. Recharge's own
   widget) writes to the same input asynchronously.
   ========================================================================== */

(function () {
  'use strict';

  var SELECTOR = '[data-cog-sub]';

  function money(value, fallback) {
    return typeof value === 'string' && value.length ? value : fallback;
  }

  function Panel(root) {
    this.root = root;
    this.form = document.getElementById(root.dataset.formId);
    this.data = {};
    this.variantId = null;
    this.mode = root.dataset.defaultMode === 'onetime' ? 'onetime' : 'subscribe';
    this.planId = null;

    var dataEl = root.querySelector('[data-cog-sub-data]');
    if (dataEl) {
      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (e) {
        // Malformed data means we cannot be sure what we'd be adding to cart.
        // Leave the panel hidden and let Recharge's widget handle it.
        this.data = {};
        this.root.hidden = true;
        return;
      }
    }

    this.els = {
      badge: root.querySelector('[data-cog-sub-badge]'),
      price: root.querySelector('[data-cog-sub-price]'),
      was: root.querySelector('[data-cog-sub-was]'),
      onetimePrice: root.querySelector('[data-cog-sub-onetime-price]'),
      chips: root.querySelector('[data-cog-sub-chips]'),
      cadence: root.querySelector('[data-cog-sub-cadence]'),
      options: root.querySelectorAll('[data-cog-sub-option]'),
      radios: root.querySelectorAll('[data-cog-sub-radio]')
    };

    // Only one subscription control should be visible. Applied from JS so that
    // if this script ever fails to run, Recharge's widget is still there.
    if (root.dataset.hideRecharge === 'true') {
      var wrapper = root.closest('.form__wrapper') || root.closest('.product-single__meta');
      if (wrapper) wrapper.classList.add('cog-sub-hide-recharge');
    }

    this.bind();
    this.syncVariantFromForm();
  }

  Panel.prototype.bind = function () {
    var self = this;

    // Whole card is clickable, but let the native label/radio do its own work.
    Array.prototype.forEach.call(this.els.options, function (option) {
      option.addEventListener('click', function (event) {
        if (event.target.closest('[data-cog-sub-chips]')) return;
        if (event.target.closest('label')) return;
        var radio = option.querySelector('[data-cog-sub-radio]');
        if (radio && !radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });

    Array.prototype.forEach.call(this.els.radios, function (radio) {
      radio.addEventListener('change', function () {
        if (!radio.checked) return;
        self.mode = radio.value === 'onetime' ? 'onetime' : 'subscribe';
        self.render();
      });
    });

    if (this.els.chips) {
      this.els.chips.addEventListener('click', function (event) {
        var chip = event.target.closest('[data-plan-id]');
        if (!chip) return;
        event.preventDefault();
        self.planId = String(chip.dataset.planId);
        self.mode = 'subscribe';
        self.render();
      });
    }

    if (this.form) {
      // Follow variant changes. The theme rewrites input[name="id"] on change,
      // so read it back on the next frame rather than guessing from options.
      this.form.addEventListener('change', function () {
        requestAnimationFrame(function () {
          self.syncVariantFromForm();
        });
      });

      // Last word before anything is added to the cart.
      this.form.addEventListener(
        'submit',
        function () {
          self.writeSellingPlan();
        },
        true
      );
      this.form.addEventListener(
        'click',
        function (event) {
          if (event.target.closest('[data-add-to-cart], [type="submit"]')) {
            self.writeSellingPlan();
          }
        },
        true
      );
    }

    // Recharge's widget injects its own selling_plan input asynchronously, so
    // the form can gain a second, competing value at any moment.
    //
    // This is deliberately state-based rather than timing-based: the observer
    // asks "is the form what it should be?" and corrects it if not. Our own
    // corrections re-enter here, find the answer is now yes, and stop. That
    // terminates on its own, with no re-entry guard to get stuck open.
    if (this.form && window.MutationObserver) {
      this.observer = new MutationObserver(function () {
        if (self.formIsCorrect()) {
          self._corrections = 0;
          return;
        }
        // If something else were to fight us for this input, stop rewriting
        // rather than spin. The submit-time sync remains the backstop.
        self._corrections = (self._corrections || 0) + 1;
        if (self._corrections > 20) return;
        self.writeSellingPlan();
      });
      this.observer.observe(this.form, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['value', 'name'],
      });
    }

    if (window.subscribe && window.theme && window.theme.PUB_SUB_EVENTS) {
      window.subscribe(window.theme.PUB_SUB_EVENTS.optionValueSelectionChange, function () {
        requestAnimationFrame(function () {
          self.syncVariantFromForm();
        });
      });
    }
  };

  Panel.prototype.syncVariantFromForm = function () {
    var input = this.form && this.form.querySelector('input[name="id"]');
    var id = input ? String(input.value) : null;

    // Fall back to the only variant we have data for.
    if (!id || !this.data[id]) {
      var keys = Object.keys(this.data);
      if (!id && keys.length === 1) id = keys[0];
    }

    this.variantId = id;
    var entry = this.data[id];

    if (!entry || !entry.plans || !entry.plans.length) {
      // This variant has no subscription option — stand down completely and
      // make sure we are not leaving a stale selling plan on the form.
      this.root.hidden = true;
      this.planId = null;
      this.writeSellingPlan();
      return;
    }

    this.root.hidden = false;

    var stillValid = entry.plans.some(function (p) {
      return String(p.id) === String(this.planId);
    }, this);

    if (!stillValid) this.planId = String(entry.plans[0].id);

    this.render();
  };

  Panel.prototype.currentEntry = function () {
    return this.data[this.variantId];
  };

  Panel.prototype.currentPlan = function () {
    var entry = this.currentEntry();
    if (!entry) return null;
    var planId = this.planId;
    var found = null;
    entry.plans.forEach(function (p) {
      if (String(p.id) === String(planId)) found = p;
    });
    return found || entry.plans[0] || null;
  };

  Panel.prototype.render = function () {
    var entry = this.currentEntry();
    if (!entry) return;
    var plan = this.currentPlan();
    if (!plan) return;

    // Selected state
    Array.prototype.forEach.call(this.els.options, function (option) {
      var isSelected = option.dataset.cogSubOption === this.mode;
      option.classList.toggle('is-selected', isSelected);
      var radio = option.querySelector('[data-cog-sub-radio]');
      if (radio) radio.checked = isSelected;
    }, this);

    // Prices
    if (this.els.price) this.els.price.textContent = money(plan.priceFormatted, '');
    if (this.els.was) this.els.was.textContent = money(entry.priceFormatted, '');
    if (this.els.onetimePrice) this.els.onetimePrice.textContent = money(entry.priceFormatted, '');
    if (this.els.badge) {
      this.els.badge.textContent = 'Save ' + plan.savePct + '%';
      this.els.badge.hidden = !(plan.savePct > 0);
    }

    this.renderChips(entry, plan);
    this.updateAtcPrice(entry, plan);
    this.writeSellingPlan();
  };

  /**
   * Keep the add-to-cart button's price in step with the panel.
   *
   * The theme renders the variant price inside the button and rewrites it on
   * variant change, so this re-applies afterwards rather than trying to stop it:
   * we run from the same variant-change events, one frame later.
   */
  Panel.prototype.updateAtcPrice = function (entry, plan) {
    if (!this.form) return;
    var el = this.form.querySelector('[data-atc-button] [data-product-price]');
    if (!el) return;

    // Panel not in play (variant has no subscription) — leave the theme's value.
    if (this.root.hidden || !entry) return;

    var wanted = this.mode === 'subscribe' && plan ? plan.priceFormatted : entry.priceFormatted;
    if (!wanted) return;
    if (el.textContent.trim() !== wanted) el.textContent = wanted;

    this.updateAtcLabel();
  };

  /**
   * "Add to Cart" becomes "Subscribe" while a subscription is selected.
   *
   * The theme's own label is captured on first run so one-time selection can
   * restore exactly what the theme rendered, rather than a hardcoded string
   * (which would break translations or a merchant-edited label).
   */
  Panel.prototype.updateAtcLabel = function () {
    if (!this.form) return;
    var wrap = this.form.querySelector('[data-add-to-cart-text]');
    if (!wrap) return;

    // The label is the wrapper's own text node, sitting alongside the price span.
    var node = null;
    for (var i = 0; i < wrap.childNodes.length; i++) {
      var n = wrap.childNodes[i];
      if (n.nodeType === 3 && n.textContent.trim().length) { node = n; break; }
    }
    if (!node) return;

    if (this._atcLabelDefault == null) this._atcLabelDefault = node.textContent;

    var subscribing = !this.root.hidden && this.mode === 'subscribe';
    var wanted = subscribing ? 'Subscribe' : this._atcLabelDefault;
    if (node.textContent !== wanted) node.textContent = wanted;
  };

  Panel.prototype.renderChips = function (entry, plan) {
    if (!this.els.chips) return;

    // A single cadence is not a choice — don't render a one-button chooser.
    if (entry.plans.length < 2) {
      this.els.chips.innerHTML = '';
      if (this.els.cadence) this.els.cadence.hidden = true;
      return;
    }
    if (this.els.cadence) this.els.cadence.hidden = false;

    var html = entry.plans
      .map(function (p) {
        var pressed = String(p.id) === String(plan.id) ? 'true' : 'false';
        var label = String(p.chip).replace(/[<>&]/g, '');
        return (
          '<button type="button" class="cog-sub__chip" data-plan-id="' +
          p.id +
          '" aria-pressed="' +
          pressed +
          '">' +
          label +
          '</button>'
        );
      })
      .join('');

    this.els.chips.innerHTML = html;
  };

  /**
   * The selling_plan value the form should be carrying right now.
   * '' means a one-time purchase, which is how Shopify reads an empty value.
   */
  Panel.prototype.wantedPlan = function () {
    if (this.root.hidden || this.mode !== 'subscribe') return '';
    var plan = this.currentPlan();
    return plan ? String(plan.id) : '';
  };

  /**
   * True when the form already carries exactly one selling_plan input with the
   * value we want (or none at all, when we want a one-time purchase).
   * This is what makes the observer self-terminating.
   */
  Panel.prototype.formIsCorrect = function () {
    if (!this.form) return true;
    var inputs = this.form.querySelectorAll('input[name="selling_plan"]');
    var wanted = this.wantedPlan();
    if (!wanted) return inputs.length === 0 || (inputs.length === 1 && inputs[0].value === '');
    return inputs.length === 1 && inputs[0].value === wanted;
  };

  /**
   * Guarantee exactly one selling_plan input in the form, carrying our value.
   * Idempotent: running it twice in a row makes no second change.
   */
  Panel.prototype.writeSellingPlan = function () {
    if (!this.form) return;

    var wanted = this.wantedPlan();
    var inputs = this.form.querySelectorAll('input[name="selling_plan"]');
    var input = inputs[0];

    // Drop duplicates so FormData can only ever serialise one value.
    for (var i = 1; i < inputs.length; i++) {
      inputs[i].parentNode.removeChild(inputs[i]);
    }

    if (!input) {
      if (!wanted) return;
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'selling_plan';
      this.form.appendChild(input);
    }

    if (input.value !== wanted) input.value = wanted;
  };

  function init(scope) {
    var roots = (scope || document).querySelectorAll(SELECTOR);
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.cogSubReady === 'true') return;
      root.dataset.cogSubReady = 'true';
      new Panel(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor: re-init when the product section is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
